import React from "react"
import { Col, List, Statistic, Row, Empty } from "antd"
import { useQuery, gql } from "@apollo/client"
import { useQueryParam, StringParam } from "use-query-params"
import { Link, Switch } from "react-router-dom"
import { Route, useRouteMatch } from "react-router-dom"
import { SearchOutlined, PlusOutlined, AppstoreOutlined, BranchesOutlined } from "@ant-design/icons"

import NewStack from "./NewStack"
import Stack from "./Stack/Stack"
import { useWorkspace } from "~/pages/Workspaces/Workspace"
import ProviderIcon from "components/atoms/ProviderIcon"
import Button from "components/atoms/Button"
import { ChangeStateIcon } from "components/molecules/StateIcon"
import Card from "components/molecules/Card"
import Input from "components/atoms/Input"

const Stacks = () => {
  const { path } = useRouteMatch()

  return (
    <Switch>
      <Route path={`${path}/new`} component={NewStack} />
      <Route path={`${path}/:stackName`} component={Stack} />
      <Route path={`${path}`} exact component={StackList} />
    </Switch>
  )
}

export const GET_WORKSPACE_STACKS = gql`
  query GetWorkspaceStacks($workspaceId: ID!) {
    workspace(id: $workspaceId) {
      id
      stacks {
        id
        createdAt
        updatedAt
        name
        description
        latestVersion {
          id
          components {
            name
          }
        }
        envs {
          id
        }
        agent {
          id
          name
          provider
        }
      }
    }
  }
`

const StackList = () => {
  const workspace = useWorkspace()
  const { url } = useRouteMatch()
  const [filter, setFilter] = useQueryParam("search", StringParam)
  const { loading, error, data } = useQuery(GET_WORKSPACE_STACKS, {
    pollInterval: 1000,
    variables: {
      workspaceId: workspace.id,
    },
  })

  if (error) return <p>Error :(</p>
  const stacks = loading ? [] : data.workspace.stacks
  const noStacks = !loading && !stacks.length

  return (
    <Card
      data-cy="stack-card"
      bordered={false}
      title="Stacks"
      bodyStyle={{ padding: "0 32px 40px 32px" }}
      extra={
        !noStacks && (
          <Row type="flex" justify="end" style={{ textAlign: "right" }}>
            <Col span={14}>
              <Input
                prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                placeholder="Search stack"
                onChange={(e) => setFilter(e.target.value)}
                value={filter}
              />
            </Col>
            <Col offset={2} span={8}>
              <Link to={`${url}/new`}>
                <Button icon={<PlusOutlined />} type="primary">
                  Create
                </Button>
              </Link>
            </Col>
          </Row>
        )
      }
    >
      {noStacks ? (
        <Empty description="No Stacks" image={Empty.PRESENTED_IMAGE_SIMPLE}>
          <Link to={`${url}/new`}>
            <Button type="primary">Create your first Stack</Button>
          </Link>
        </Empty>
      ) : (
        <List
          size="large"
          rowKey="id"
          loading={loading}
          dataSource={stacks.filter((p) => (filter ? p.name.toLowerCase().includes(filter.toLowerCase()) : true))}
          pagination={{
            defaultCurrent: 1,
          }}
          renderItem={(stack) => <ListContent stack={stack} />}
        />
      )}
    </Card>
  )
}

const ListContent = ({ stack }) => {
  const { url } = useRouteMatch()

  let lastJobState = "COMPLETED"

  return (
    <List.Item data-cy={`stack-${stack.name}`} actions={[<Link to={`${url}/${stack.name}`}>View</Link>]}>
      <List.Item.Meta
        avatar={<ChangeStateIcon state={lastJobState} size="large" />}
        title={<Link to={`${url}/${stack.name}`}>{stack.name}</Link>}
        description={stack.description || "No description"}
      />

      <Col span={5}>
        <Statistic title="Agent" value={stack.agent.name} prefix={<ProviderIcon provider={stack.agent.provider} />} />
      </Col>

      <Col span={5}>
        <Statistic title="Envs" value={stack.envs.length} prefix={<BranchesOutlined />} />
      </Col>
      <Col span={5}>
        <Statistic title="Components" value={stack.latestVersion.components.length} prefix={<AppstoreOutlined />} />
      </Col>
    </List.Item>
  )
}

export default Stacks
